/**
 * Qué texto se le muestra al usuario cuando el backend rechaza un informe.
 *
 * Por defecto Informes traduce el `error_code` contra un mapa de i18n y descarta el
 * `error_message` del server — es la política del Frontend (aislamiento cross-agency: un mensaje
 * crudo puede nombrar cosas de otra agencia, o llegar en inglés desde DRF).
 *
 * La excepción son los errores del VALIDADOR DE LAYOUT (`reports/services/layout_schema.py`):
 * textos escritos a mano en el backend, sobre la hoja que el usuario tiene delante, sin un solo
 * dato de nadie. Ahí el mensaje del server es lo único que dice QUÉ arreglar, y descartarlo salía
 * caro: una plantilla de 24 métricas contra un tope de 20 devolvía `INVALID_PANEL` con
 * «Un panel admite hasta 20 métricas.», y la pantalla mostraba «le falta una dimensión o una
 * métrica» — le decía al usuario que le FALTABAN campos cuando le SOBRABAN. Pablo la hizo cargar
 * deseleccionando métricas a ciegas, sin saber por qué funcionaba.
 *
 * No incluye los errores de panel (`PanelState`): ésos vienen del motor de queries con un
 * `public_message()` ya saneado por el backend y nunca son del validador.
 */

export const SERVER_MESSAGE_CODES = new Set([
    'INVALID_PANEL',
    'INVALID_PANEL_TYPE',
    'INVALID_LAYOUT',
    'DUPLICATE_PANEL_ID',
    'PANEL_CARRIES_SETTINGS',
    'TOO_MANY_QUERIES',
]);

/**
 * @param error {code, message} tal como lo devuelve `errorOf()`.
 * @param translateCode (code) => string — resuelve el código en el namespace de i18n de quien
 *        llama (el banner y el toast tienen mapas distintos).
 * @param language el idioma activo de la UI (`i18n.language`). Los mensajes del validador están
 *        escritos en español en el backend, así que sólo se muestran con la UI en español: en
 *        inglés mostrarlos metería un texto español en una pantalla en inglés. El texto de i18n al
 *        que se cae ya no afirma una causa que no conoce, así que el fallback es honesto —
 *        pierde el número, no lo inventa. La salida definitiva es que el backend mande
 *        `error_params` (`{max: 20}`) y el copy se arme acá, en las dos lenguas.
 */
export function errorText(error, translateCode, language) {
    const code = (error && error.code) || null;
    // `serverMessage` es el `error_message` del cuerpo, no el `message` que arma `APIClient`: ése
    // antepone el `detail` de DRF y, si no hay ninguno, inventa un genérico. Los dos terminarían en
    // pantalla como si fueran el motivo del validador. Sin cuerpo crudo no se muestra nada del
    // server, que es el lado seguro para equivocarse.
    const message = error && error.serverMessage;
    if (message && SERVER_MESSAGE_CODES.has(code) && isSpanish(language)) return message;
    return translateCode(code);
}

// Sin idioma declarado se asume español, que es la lengua por defecto del producto y el
// comportamiento que se verificó en pantalla.
function isSpanish(language) {
    return language === undefined || language === null || String(language).toLowerCase().startsWith('es');
}
